/**
 * Ported from the lekterion codebase (`src/main.tsx`) @ `d942f2f`.
 * Bindery is canonical for this module from 2026-09-06; reconcile source-side
 * drift toward this copy.
 *
 * The source repo built the provider stack, the router and the root render in
 * one `main.tsx`. This workspace splits them: everything from the providers
 * down lives here, and `src/main.tsx` only mounts it. `docs/convex.md` gives
 * the reason — `scripts/prerender.tsx` renders `<App />` and nothing above
 * it, so a Convex provider mounted in the entry would leave every prerendered
 * page without one and `useConvex()` throws when there is none. This app is
 * `APP_TYPE: "spa"` and has no prerender pass, so nothing depends on that
 * today; the split is here so that flipping the app type later is not also a
 * Convex change.
 */
import { ConvexProviderWithAuth } from "convex/react";
import { Loader2 } from "lucide-react";
import { lazy, Suspense, type JSX } from "react";
import { createBrowserRouter, RouterProvider } from "react-router";
import { Toaster } from "@bindery/ui/components/ui/sonner";
import { AuthProvider } from "@/auth/AuthProvider";
import { useConvexLightAuth } from "@/auth/context";
import { AutoplayProvider } from "@/components/autoplay-provider";
import { LanguageProvider } from "@/components/language-provider";
import { ThemeProvider } from "@/components/theme-provider";
import { convex } from "@/convexClient";
import { Library } from "@/library/Library";

// Library is the "/" landing page, so it stays eager. The reader, admin, and
// auth-callback routes are split into their own chunks (loaded on demand) to
// keep the initial bundle under Vite's 500 kB warning — the reader alone pulls
// in the whole src/read/ tree plus radix popover/dropdown/sheet primitives.
const AdminPage = lazy(() =>
  import("@/auth/AdminPage").then((m) => ({ default: m.AdminPage })),
);
const AuthCallback = lazy(() =>
  import("@/auth/AuthCallback").then((m) => ({ default: m.AuthCallback })),
);
const ReadingView = lazy(() =>
  import("@/read/ReadingView").then((m) => ({ default: m.ReadingView })),
);

function RouteFallback(): JSX.Element {
  return (
    <div className="flex min-h-svh items-center justify-center">
      <Loader2 className="text-muted-foreground size-6 animate-spin" />
    </div>
  );
}

// Convex backs the AI gloss/translate (§5) + TTS (§10) cache. Reading,
// navigation, citation, and copy (§1–§3) are pure Tier-1 and need no backend.
// Auth (§8) flows through light-auth: ConvexProviderWithAuth attaches the JWT so
// gated actions see an identity; anonymous reading still works without one.
//
// The client itself is `@/convexClient` — module scope, one per app, never
// rebuilt in render. See that file for why.
const router = createBrowserRouter([
  { path: "/", element: <Library /> },
  { path: "/read/:textId", element: <ReadingView /> },
  { path: "/auth/callback", element: <AuthCallback /> },
  { path: "/admin", element: <AdminPage /> },
]);

export function App(): JSX.Element {
  return (
    <AuthProvider>
      <ConvexProviderWithAuth client={convex} useAuth={useConvexLightAuth}>
        <ThemeProvider>
          <LanguageProvider>
            <AutoplayProvider>
              <Suspense fallback={<RouteFallback />}>
                <RouterProvider router={router} />
              </Suspense>
              <Toaster position="bottom-center" />
            </AutoplayProvider>
          </LanguageProvider>
        </ThemeProvider>
      </ConvexProviderWithAuth>
    </AuthProvider>
  );
}
